import { Box, IconButton, TextField, Typography } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import { useGlPostingAccountOptions } from "../hooks/useGlPostingAccountOptions"
import type { GlTransactionFormValues } from "../schemas/glTransactionForm"

import { Button } from "@/components/common/Button"
import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { GlTransactionRowType } from "@/graphql/codegen/graphql"

const TRANSACTION_TYPE_OPTIONS = [
  { value: GlTransactionRowType.Debit, label: "Debit" },
  { value: GlTransactionRowType.Credit, label: "Credit" },
]

const DEFAULT_LINE = { type: GlTransactionRowType.Debit, glAccountId: null, amount: null }

const formatCurrency = (value: number): string => value.toLocaleString("en-US", { style: "currency", currency: "USD" })

interface TransactionLinesSectionProps {
  disabled?: boolean
  initialSelectedOptions?: Record<number, { value: string; label: string }>
}

export const TransactionLinesSection: FC<TransactionLinesSectionProps> = ({ disabled, initialSelectedOptions }) => {
  const { control, trigger } = useFormContext<GlTransactionFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: "transactionLines" })

  const transactionLines = useWatch({ control, name: "transactionLines" })

  const { glPostingAccounts } = useGlPostingAccountOptions()

  // Track selected options per row to keep them in the list during search
  const [selectedOptions, setSelectedOptions] = useState<Record<number, { value: string; label: string } | null>>(
    initialSelectedOptions ?? {}
  )

  const getOptionsForRow = useCallback(
    (index: number) => {
      const selectedInOtherRows = new Set(
        transactionLines.filter((line, i) => i !== index && Boolean(line.glAccountId)).map((line) => line.glAccountId)
      )
      const mapped = glPostingAccounts
        .filter((account) => !selectedInOtherRows.has(account.id))
        .map((account) => ({ value: account.id, label: account.label }))
      const selected = selectedOptions[index]
      if (selected && !mapped.some((o) => o.value === selected.value)) {
        mapped.unshift(selected)
      }
      return mapped
    },
    [glPostingAccounts, selectedOptions, transactionLines]
  )

  const handleAccountChange = useCallback(
    (index: number, value: string | null) => {
      if (!value) {
        setSelectedOptions((prev) => ({ ...prev, [index]: null }))
        return
      }
      const option = glPostingAccounts.find((a) => a.id === value)
      if (option) {
        setSelectedOptions((prev) => ({ ...prev, [index]: { value: option.id, label: option.label } }))
      }
    },
    [glPostingAccounts]
  )

  const totalDebits = useMemo(
    () =>
      transactionLines
        .filter((line) => line.type === GlTransactionRowType.Debit)
        .reduce((acc, line) => acc + (line.amount ?? 0), 0),
    [transactionLines]
  )

  const totalCredits = useMemo(
    () =>
      transactionLines
        .filter((line) => line.type === GlTransactionRowType.Credit)
        .reduce((acc, line) => acc + (line.amount ?? 0), 0),
    [transactionLines]
  )

  const difference = totalDebits - totalCredits
  const isBalanced = Math.abs(difference) < 0.001
  const debitCount = transactionLines.filter((line) => line.type === GlTransactionRowType.Debit).length
  const creditCount = transactionLines.filter((line) => line.type === GlTransactionRowType.Credit).length

  const handleAddLine = useCallback(async () => {
    append({ ...DEFAULT_LINE })
    await trigger("transactionLines")
  }, [append, trigger])

  const canRemoveLine = (index: number): boolean => {
    const lineType = transactionLines[index]?.type
    switch (lineType) {
      case GlTransactionRowType.Debit:
        return debitCount > 1
      case GlTransactionRowType.Credit:
        return creditCount > 1
      default:
        return fields.length > 2
    }
  }

  return (
    <Box className='flex flex-col gap-3'>
      <Box className='grid grid-cols-[160px_1fr_160px_40px] gap-2 px-1'>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Type
        </Typography>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Account
        </Typography>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Amount
        </Typography>
        <span />
      </Box>

      {fields.map((field, index) => (
        <Box key={field.id} className='grid grid-cols-[160px_1fr_160px_40px] items-start gap-2'>
          <FormSelect
            control={control}
            name={`transactionLines.${index}.type`}
            options={TRANSACTION_TYPE_OPTIONS}
            label=''
            disabled={disabled}
            data-testid={`gl-transaction.form-dialog.line-type-${index}`}
          />
          <FormAutocomplete
            control={control}
            name={`transactionLines.${index}.glAccountId`}
            label=''
            placeholder='Search account'
            options={getOptionsForRow(index)}
            required
            onChange={(value) => handleAccountChange(index, value)}
            disabled={disabled}
            data-testid={`gl-transaction.form-dialog.line-account-${index}`}
          />
          <FormNumericField
            control={control}
            name={`transactionLines.${index}.amount`}
            label=''
            decimalScale={2}
            allowNegative={false}
            disabled={disabled}
            data-testid={`gl-transaction.form-dialog.line-amount-${index}`}
          />
          <IconButton
            size='small'
            disabled={disabled || !canRemoveLine(index)}
            onClick={() => remove(index)}
            data-testid={`gl-transaction.form-dialog.line-remove-button-${index}`}
            className='mt-1'
          >
            <DeleteOutlined fontSize='small' />
          </IconButton>
        </Box>
      ))}

      <Box className='flex justify-end'>
        <Box className='flex flex-col items-end gap-1'>
          {!disabled && (
            <Button
              appearance='text'
              startIcon={<AddOutlined />}
              onClick={handleAddLine}
              data-testid='gl-transaction.form-dialog.add-line-button'
              className='w-[260px]'
            >
              Add Another Transaction
            </Button>
          )}

          <TextField
            label='Balance'
            value={formatCurrency(Math.abs(difference))}
            disabled
            size='small'
            data-testid='gl-transaction.form-dialog.difference'
            error={!isBalanced && totalDebits > 0 && totalCredits > 0}
            sx={{
              width: 260,
              "& .MuiInputBase-input.Mui-disabled": {
                color: isBalanced ? "success.dark" : "error.main",
                WebkitTextFillColor: isBalanced ? undefined : "inherit",
              },
            }}
          />
        </Box>
      </Box>

      {!isBalanced && totalDebits > 0 && totalCredits > 0 && (
        <Typography variant='caption' className='text-red-600'>
          Total debits must equal total credits.
        </Typography>
      )}
    </Box>
  )
}
